/**
 * Copyright 2020 New Light Technologies, Inc.
 *
 * With Supporting Sponsorship from the Federal Emergency Management Agency (Contract: GSA Stars II GS-06F-0968Z)
 * In accordance with FAR 52.227-14(c)(iii), New Light Technologies, Inc. grants to the Government and others acting on its behalf a paid-up, nonexclusive, irrevocable, worldwide license in such copyrighted computer software and data to reproduce, prepare derivative works, and perform publicly and display publicly (but not to distribute copies to the public) by or on behalf of the Government.
 * Any other use, distribution, reproduction, modification, or publication without the prior express written authorization of New Light Technologies, Inc. is strictly prohibited.
 * All other rights are reserved by their respective copyright holders.
 *
 */
import type { Feature, FeatureCollection, Geometry, Polygon } from 'geojson';
import { wowChunksSelector } from './selectors';
import { WowProperties, WowPostProperties, CategoryID } from './types';
import { POST_EVENT_OF, primaryKeyForSection } from '../Post/PostLayer';
import { useGridCellLayer } from '../map/GridCellLayer';
import { useAtom, useAtomValue } from 'jotai';
import { loadable } from '../utils/loadable';
import React from 'react';
import { Legend } from '../components/Legend';
import type { RGBColor, RGBAColor } from '../map/deckgl-types';
import { Box } from '@mui/material';
import { selectedFeatureAtom } from '../Incident/FeaturePopup';
import { Toast } from '../components/Toast';

type Props = {
  extruded: boolean;
  visible: boolean;
  /**
   * Whether this mount needs the WoW tables at all. The hook is mounted by the
   * shared Map component on every route; `false` (incident views, where these
   * layers never draw) skips the fetch instead of hiding its result.
   */
  enabled: boolean;
};

export const PERIMETER_FILL_ALPHA = 100;
export const FORECAST_LIGHTEN_FACTOR = 0.4;

export function lightenColor(color: RGBColor, factor: number): RGBColor {
  return [
    Math.round(color[0] + (255 - color[0]) * factor),
    Math.round(color[1] + (255 - color[1]) * factor),
    Math.round(color[2] + (255 - color[2]) * factor),
  ];
}

/**
 * Splits a collection into the features in a WoW time category and every other
 * feature, so a caller drawing both halves loses no row to an unexpected value.
 */
export function partitionByTimeCategory<
  G extends Geometry,
  P extends { time_category?: string } | null,
>(
  collection: FeatureCollection<G, P>,
  timeCategory: 'future' | 'past'
): { matching: FeatureCollection<G, P>; rest: FeatureCollection<G, P> } {
  const matching: Feature<G, P>[] = [];
  const rest: Feature<G, P>[] = [];
  for (const feature of collection.features) {
    (feature.properties?.time_category === timeCategory ? matching : rest).push(feature);
  }
  return {
    matching: { ...collection, features: matching },
    rest: { ...collection, features: rest },
  };
}

export function filterPerimetersByTimeCategory(
  chunks: FeatureCollection<Polygon, WowProperties>[],
  timeCategory: 'future' | 'past'
): FeatureCollection<Polygon, WowProperties>[] {
  return chunks.map(chunk => partitionByTimeCategory(chunk, timeCategory).matching);
}

export function makePerimeterFillColor(
  colorTransform?: (color: RGBColor) => RGBColor
): (d: Feature<Polygon, WowProperties>) => RGBAColor {
  return (d: Feature<Polygon, WowProperties>): RGBAColor => {
    const color = lookupCategoryColor(d.properties.categoryid);
    if (!color) {
      console.warn('Unknown WoW CategoryID:', d.properties.categoryid);
      return [0, 0, 0, 0];
    }
    const transformed = colorTransform ? colorTransform(color) : color;
    return [...transformed, PERIMETER_FILL_ALPHA];
  };
}

export const useWowLayer = (props: Props) => {
  const [selectedPost] = useAtom(selectedFeatureAtom);
  const primaryKey = useAtomValue(primaryKeyForSection('wow-post'));
  const wowPerimeters = useAtomValue(
    loadable(wowChunksSelector({ type: 'perimeter', enabled: props.enabled }))
  );

  const wowPost = useAtomValue(
    loadable(wowChunksSelector({ type: 'post', enabled: props.enabled }))
  );

  const handlePostFillColor = React.useMemo(
    () => (primaryKey === 'priority_rank' ? getPostFillColor : undefined),
    [primaryKey]
  );

  const handleFilterValue = React.useCallback(() => 0, []);

  const WowGridCellLayer = useGridCellLayer<Feature<Polygon, WowPostProperties>>({
    id: 'wow-post',
    // @ts-expect-error
    data: wowPost.state === 'hasData' ? wowPost.data : [],
    getFillColor: handlePostFillColor,
    eventOf: POST_EVENT_OF.wow,
    extruded: props.extruded,
    visible: props.visible && wowPost.state === 'hasData',
    highlightId: selectedPost?.id,
    // onClick: handleClick,
  });

  const perimeterData: FeatureCollection<Polygon, WowProperties>[] = React.useMemo(
    () =>
      (wowPerimeters.state === 'hasData' ? wowPerimeters.data : []) as FeatureCollection<
        Polygon,
        WowProperties
      >[],
    [wowPerimeters]
  );

  const forecastData = React.useMemo(
    () => filterPerimetersByTimeCategory(perimeterData, 'future'),
    [perimeterData]
  );

  const observedData = React.useMemo(
    () => filterPerimetersByTimeCategory(perimeterData, 'past'),
    [perimeterData]
  );

  const handleForecastFillColor = React.useMemo(
    () => makePerimeterFillColor(c => lightenColor(c, FORECAST_LIGHTEN_FACTOR)),
    []
  );
  const handleObservedFillColor = React.useMemo(() => makePerimeterFillColor(), []);

  const makeLineColor = React.useCallback(
    (colorTransform?: (color: RGBColor) => RGBColor) =>
      (d: { id?: string | number; properties: { categoryid: CategoryID } }): RGBAColor => {
        if (d.id === selectedPost?.id) {
          return [255, 255, 255, 255];
        }

        const color = lookupCategoryColor(d.properties.categoryid);
        if (!color) {
          console.warn('Unknown WoW CategoryID:', d.properties.categoryid);
          return [0, 0, 0, 0];
        }
        const transformed = colorTransform ? colorTransform(color) : color;
        return [...transformed, 255];
      },
    [selectedPost?.id]
  );

  const perimeterVisible = props.visible && wowPerimeters.state === 'hasData';

  const forecastLineColor = React.useMemo(
    () => makeLineColor(c => lightenColor(c, FORECAST_LIGHTEN_FACTOR)),
    [makeLineColor]
  );

  const observedLineColor = React.useMemo(() => makeLineColor(), [makeLineColor]);

  const WowForecastPerimeterLayer = useGridCellLayer<Feature<Polygon, WowProperties>>({
    id: 'wow-forecast-perimeter',
    // @ts-expect-error
    data: forecastData,
    pickable: true,
    getFilterValue: handleFilterValue,
    getFillColor: handleForecastFillColor,
    getLineColor: d => forecastLineColor({ id: d.id, properties: d.properties }),
    extruded: false,
    highlightId: selectedPost?.id,
    visible: perimeterVisible,
  });

  const WowObservedPerimeterLayer = useGridCellLayer<Feature<Polygon, WowProperties>>({
    id: 'wow-observed-perimeter',
    // @ts-expect-error
    data: observedData,
    pickable: true,
    getFilterValue: handleFilterValue,
    getFillColor: handleObservedFillColor,
    getLineColor: d => observedLineColor({ id: d.id, properties: d.properties }),
    extruded: false,
    highlightId: selectedPost?.id,
    visible: perimeterVisible,
  });

  const layers = React.useMemo(
    () => [WowForecastPerimeterLayer, WowObservedPerimeterLayer, WowGridCellLayer],
    [WowForecastPerimeterLayer, WowObservedPerimeterLayer, WowGridCellLayer]
  );

  return layers;
};

export const wowColorMap: Record<CategoryID, RGBColor> = {
  earthquake1: [139, 69, 19],
  rain1: [127, 23, 14],
  rain2: [1, 255, 0],
  rain3: [74, 137, 92],
  rain4: [56, 118, 106],
  rain5: [0, 139, 189],
  rain6: [30, 80, 140],
  tornado1: [250, 1, 2],
  tornado2: [255, 254, 0],
  tornado3: [255, 69, 0],
  wildfire1: [181, 9, 9],
  wildfire2: [255, 221, 176],
  wildfire3: [253, 68, 3],
  winter1: [123, 104, 238],
  winter2: [74, 212, 240],
};

// Own-property lookup, not bare indexing: categoryid arrives from the WoW feed,
// so its CategoryID type is an assertion about the data rather than a
// guarantee, and a value like `constructor` would otherwise resolve up the
// prototype chain to a function the callers spread as a colour tuple. An
// uncovered category reads back undefined, exactly as it did.
export const lookupCategoryColor = (categoryid: string): RGBColor | undefined =>
  Object.hasOwn(wowColorMap, categoryid) ? wowColorMap[categoryid as CategoryID] : undefined;

const postColorRange: RGBAColor[] = [
  [181, 9, 9, 190], // #B50909
  [219, 129, 0, 190], // #DB8100
  [226, 178, 0, 190], // #E2B200
  [143, 195, 201, 190], // #8FC3C9
  [95, 133, 188, 190], // #5F85BC
];

export const getPostFillColor = (d: Feature<Polygon, WowPostProperties>): RGBAColor => {
  const { priority_rank } = d.properties;
  switch (priority_rank) {
    case 1:
      return postColorRange[0];
    case 2:
      return postColorRange[1];
    case 3:
      return postColorRange[2];
    case 4:
      return postColorRange[3];
    case 5:
      return postColorRange[4];
    default:
      return [0, 0, 0, 0];
  }
};

type WowRecord = Record<
  CategoryID,
  {
    pastCount: number;
    futureCount: number;
    category: string;
  }
>;

export const WowLegend = React.memo(() => {
  // Rendered only where WoW is the selected map (home dashboard / wowcast), so
  // the data this legend counts is data that view already needs.
  const wowPerimeters = useAtomValue(
    loadable(wowChunksSelector({ type: 'perimeter', enabled: true }))
  );

  const eventCount = React.useMemo(() => {
    if (wowPerimeters.state === 'hasError' || wowPerimeters.state === 'loading') {
      return null;
    }

    return wowPerimeters.data.reduce<Partial<WowRecord>>((acc, chunk) => {
      const chunkEvents = chunk.features.reduce<Partial<WowRecord>>((chunkAcc, event) => {
        const { categoryid, category, time_category } = event.properties as WowProperties;
        if (!Object.hasOwn(wowColorMap, categoryid)) {
          return acc;
        }
        if (Object.hasOwn(acc, categoryid)) {
          const chunk = chunkAcc[categoryid];
          if (chunk != null) {
            if (time_category === 'future') {
              chunk.futureCount++;
            }
            if (time_category === 'past') {
              chunk.pastCount++;
            }
          }
        } else {
          if (time_category === 'future') {
            chunkAcc[categoryid] = {
              futureCount: 1,
              pastCount: 0,
              category,
            };
          }
          if (time_category === 'past') {
            chunkAcc[categoryid] = {
              futureCount: 0,
              pastCount: 1,
              category,
            };
          }
        }
        return acc;
      }, acc);

      return chunkEvents;
    }, {});
  }, [wowPerimeters]);

  if (eventCount == null) {
    return null;
  }

  const events = Object.keys(eventCount).sort();

  const forecastedEvents = events.filter(
    event => (eventCount[event as CategoryID]?.futureCount ?? 0) > 0
  );

  const observationEvents = events.filter(
    event => (eventCount[event as CategoryID]?.pastCount ?? 0) > 0
  );

  return (
    <>
      {forecastedEvents.length > 0 && (
        <Box
          sx={{
            pb: observationEvents.length > 0 ? 1 : 0,
          }}
        >
          <Legend
            id="wow-forecast-perimeter"
            title="WOW Forecast Perimeters"
            items={forecastedEvents.map(event => {
              const wowRecord = eventCount[event as CategoryID];
              const lightened = lightenColor(
                wowColorMap[event as CategoryID],
                FORECAST_LIGHTEN_FACTOR
              );
              return {
                label: wowRecord?.category ?? '',
                color: `rgb(${lightened})`,
                value: wowRecord?.futureCount,
              };
            })}
          />
        </Box>
      )}
      <Toast
        open={events.length === 0}
        message="No current WoW (Worst of the Worst) forecasts or incidents."
        severity="success"
        autoHideDuration={10000}
      />
      {observationEvents.length > 0 && (
        <Legend
          id="wow-observed-perimeter"
          title="WOW Observation Perimeters"
          items={observationEvents.map(event => {
            const wowRecord = eventCount[event as CategoryID];
            return {
              label: wowRecord?.category ?? '',
              color: `rgb(${wowColorMap[event as CategoryID]})`,
              value: wowRecord?.pastCount,
            };
          })}
        />
      )}
    </>
  );
});
